/**
 * @fileoverview One session list, two places: a program's Sessions tab and the
 * org Dashboard's Lessons tab. Both list the rows `org_analytics` builds (a row
 * per class meeting, plus each solo lesson), so they share the row model, the
 * one word each status reads as, and the filter options — the org-wide list is
 * the program list with every program in it, never a second format.
 */

import { csvEscape, lessonTime, sortChronologically } from "@/lib/lessonHistory";

/* The kinds a session list filters by: the tile's own split. `student` is the
   model's word for a solo lesson (a row whose kind is not "class"). */
export const SESSION_KINDS = [
  { id: "all", label: "All" },
  { id: "class", label: "Class sessions" },
  { id: "student", label: "Solo lessons" },
];

export const SESSION_STATUSES = [
  { id: "all", label: "All" },
  { id: "written", label: "Taught" },
  { id: "missing", label: "Missing notes" },
  { id: "cleared", label: "Notes cleared" },
  { id: "upcoming", label: "Upcoming" },
  { id: "archived", label: "Archived" },
];

/* A tile's trailing window, offered as a period so a list can open on
   exactly the rows the tile counted; the ordinary periods follow. */
export const WINDOW_PERIOD = "window";

/* The Upcoming tile's window (today and the 29 days after it, at the venue),
   offered the same way. */
export const UPCOMING_PERIOD = "upcoming";

/* What "still to come" means, the server's `UPCOMING_STATUSES`: a meeting
   nobody has written against yet, or one written ahead of its day. */
const UPCOMING = new Set(["scheduled", "planned", "planned (unscheduled)"]);

/** Whether a session row (`sessionRow`) is still to come. */
export const isUpcoming = (row) => UPCOMING.has(row.session_status);

/**
 * A session list's rows for its status filter. "upcoming" is two statuses at
 * once, so it is applied here and the shared filter then sees "all"; every
 * other status passes through to `filterLessons` untouched.
 *
 * An archived session (useOrgSessionActions) is listed under "archived" alone:
 * every other choice, "all" included, leaves it out.
 *
 * @returns {{ rows: Array, status: string }}
 */
export function byStatus(rows, status) {
  if (status === "archived") return { rows: rows.filter((r) => r.archived), status: "all" };
  const listed = rows.filter((r) => !r.archived);
  return status === "upcoming" ? { rows: listed.filter(isUpcoming), status: "all" } : { rows: listed, status };
}

/** The URL filter word a status reads as, and back. */
export const STATUS_FILTERS = { written: "taught", upcoming: "upcoming", missing: "missing", cleared: "cleared", archived: "archived" };
const FROM_FILTER = Object.fromEntries(Object.entries(STATUS_FILTERS).map(([status, word]) => [word, status]));
export const statusFromFilter = (filter) => FROM_FILTER[filter] || "all";

export const STATUS_TAGS = {
  "no notes": { label: "No notes", accent: true },
  "notes cleared": { label: "Notes cleared" },
  planned: { label: "Planned" },
  "planned (unscheduled)": { label: "Planned" },
  scheduled: { label: "Upcoming" },
  cancelled: { label: "No class" },
  "taught (unscheduled)": { label: "Unscheduled" },
};

/** "Sep 22, 2026" from YYYY-MM-DD without tripping over UTC parsing. */
export function shortDate(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y) return ymd || "";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** A session row as the shared lesson filter (`filterLessons`) reads it. */
export function sessionRow(r) {
  return {
    ...r,
    lesson_id: r.lesson_id || `${r.kind}:${r.class_id || "solo"}:${r.date}:${r.time_local || ""}`,
    kind: r.kind === "class" ? "class" : "student",
    user_id: r.instructor_id,
    created_at: `${r.date}T12:00:00`,
    scheduled_date: r.date,
    planned: String(r.status).startsWith("planned"),
    missing: r.status === "no notes",
    // Its notice cleared (lib/notesClearance): neither taught nor missing.
    notes_cleared: r.status === "notes cleared" || undefined,
    scheduled: r.status === "scheduled",
    status: r.status === "cancelled" ? "cancelled" : undefined,
    session_status: r.status,
    // Whether an instructor's lesson stands behind the row, rather than a
    // meeting alone: the server's own `lesson_id`, before one is made up above.
    has_lesson: Boolean(r.lesson_id),
    archived: Boolean(r.archived),
  };
}

/**
 * The distinct `{id, name}` pairs a list's rows carry, in first-seen order —
 * the options of its instructor, class or program picker.
 */
export function distinctOptions(rows, idOf, nameOf) {
  const seen = new Map();
  for (const r of rows) {
    const id = idOf(r);
    if (id && !seen.has(id)) seen.set(id, nameOf(r) || id);
  }
  return [...seen.entries()].map(([id, name]) => ({ id, name }));
}

/** Rows inside an inclusive `YYYY-MM-DD` window, or every row without one. */
export function inWindow(rows, win) {
  if (!win?.start || !win?.end) return rows;
  return rows.filter((r) => r.date >= win.start && r.date <= win.end);
}

/**
 * The columns the dashboard's Lessons export writes, in order — the server's
 * `LESSON_EXPORT_COLUMNS` (services/org_analytics.py): what the list says
 * about each session, never what was said in it. Exporting a selection writes
 * the same columns, so a ticked handful and the whole tab come out alike.
 * `session_status` is the server's `status`: `sessionRow` keeps the server's
 * word there.
 */
export const SESSION_EXPORT_COLUMNS = [
  ["date", "Date"],
  ["time_local", "Time"],
  ["program_title", "Program"],
  ["class_name", "Class"],
  ["instructor_name", "Instructor"],
  ["substitute_name", "Substitute"],
  ["session_status", "Status"],
  ["attendance", "Attendance"],
  ["roster_size", "Roster size"],
  ["minutes", "Minutes"],
  ["title", "Lesson title"],
  ["shared", "Shared with families"],
  ["archived", "Archived"],
];

/**
 * Session rows as CSV text, oldest first like the list and the server's
 * export, every cell through the formula guard. `toRow` maps a list's own row
 * shape onto the export's fields (a class page's lessons are not
 * `sessionRow`s).
 */
export function sessionsCsv(rows, toRow = (r) => r) {
  const header = SESSION_EXPORT_COLUMNS.map(([, label]) => csvEscape(label)).join(",");
  const lines = sortChronologically(rows, lessonTime).map((r) => {
    const row = toRow(r);
    // A flag reads yes or blank, as the server's export writes it.
    return SESSION_EXPORT_COLUMNS.map(([key]) => csvEscape(typeof row[key] === "boolean" ? (row[key] ? "yes" : "") : row[key])).join(",");
  });
  return [header, ...lines].join("\r\n");
}

/** How many classes one bulk chase names: the server's page (`NOTES_CHASE_BATCH`). */
export const NOTES_CHASE_BATCH = 50;

/**
 * Who a chase of the ticked sessions that owe notes reaches (C4): their
 * classes, and the instructors the rows name (each class's teachers, else who
 * it is filed under). `known` is false when a row names nobody, so the bar
 * does not promise a count it cannot back (mobile/src/lib/orgSessions.js).
 *
 * @param {Array<object>} owing
 * @returns {{classIds: string[], instructors: number, known: boolean}}
 */
export function chaseReach(owing) {
  const classIds = [...new Set((owing || []).map((r) => r.class_id).filter(Boolean))];
  const people = new Set();
  let known = true;
  for (const row of owing || []) {
    const ids = (row.instructor_ids?.length ? row.instructor_ids : [row.user_id || row.instructor_id]).filter(Boolean);
    if (!ids.length) known = false;
    ids.forEach((id) => people.add(id));
  }
  return { classIds, instructors: people.size, known: known && people.size > 0 };
}

/**
 * What a chase reports, from the instructors the server reached: "Asked Maya
 * Ortiz and Sam Lee for notes", or that there was nobody to ask.
 *
 * @param {Array<{name?: string}>} chased
 * @returns {string}
 */
export function chaseOutcome(chased) {
  const names = (chased || []).map((p) => p.name).filter(Boolean);
  if (!names.length) return "Nobody is teaching these classes to ask";
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `Asked ${list} for notes`;
}
